import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { InlineMessage } from '@crowdex/ui/components/feedback';

import { useCallback, useEffect, useState } from 'react';
import {
  SettingRow,
  SettingSelect,
  SettingsGroup,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import { usePluginWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useRouter } from '@/lib/navigation';
import { pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';

export type ListedModel = {
  id: string;
  name: string;
  provider: string;
  providerTitle: string;
  output: string[];
};
type Choice = { provider: string; model: string };
type Listing = {
  models: ListedModel[];
  /** Providers whose models could not be listed, and why. */
  problems?: { provider: string; providerTitle: string; message: string }[];
  defaults: Record<string, Choice>;
};

/** The Crowlingo application every model comes through (ADR 0004): its keys are set up there. */
const MODELS_SERVICE = 'crowpus';

/** The outputs a default model is chosen for. */
const OUTPUTS = [
  { output: 'text', title: 'Text' },
  { output: 'image', title: 'Images' },
  { output: 'speech', title: 'Voice-over' },
  { output: 'transcription', title: 'Transcription' },
] as const;

/** How an output reads in a model's line. */
const OUTPUT_LABELS: Record<string, string> = {
  text: 'text',
  image: 'images',
  speech: 'voice',
  transcription: 'transcription',
  'realtime-transcription': 'live transcription',
};

/** The voice outputs, which Settings › Voice lists. */
export const VOICE_OUTPUTS: readonly string[] = [
  'speech',
  'transcription',
  'realtime-transcription',
];

/** The core's pick: the first model that makes it. */
const AUTOMATIC = 'automatic';
/** A model and its provider, as one value of a picker. */
const pickerValue = (choice: Choice) => `${choice.provider}\n${choice.model}`;
const message = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

/** How many rows a list shows before "Show all"; one more row is shown rather than hidden. */
const FIRST = 3;

/** The models offered (through Crowpus, or a provider's own key), a few first, the rest on demand. */
export function AvailableModels({
  title,
  models,
}: {
  title: string;
  models: readonly ListedModel[];
}) {
  const [open, setOpen] = useState(false);
  if (models.length === 0) return null;
  const cut = models.length > FIRST + 1;
  const shown = cut && !open ? models.slice(0, FIRST) : models;
  return (
    <SettingsGroup
      aside={
        cut ? (
          <button
            className="text-muted-foreground text-xs hover:text-foreground"
            onClick={() => setOpen(!open)}
            type="button"
          >
            {open ? 'Show less' : `Show all · ${models.length}`}
          </button>
        ) : undefined
      }
      title={title}
    >
      {shown.map((model) => (
        <SettingRow
          description={[
            model.providerTitle,
            model.output.map((output) => OUTPUT_LABELS[output] ?? output).join(', '),
          ].join(' · ')}
          key={`${model.provider}/${model.id}`}
          title={model.name}
        />
      ))}
    </SettingsGroup>
  );
}

/** No model is offered: Crowpus is where the organization sets them up. */
export function NoModels({ title }: { title: string }) {
  const router = useRouter();
  // Settings has no workspace: the user's plugins.
  const crowpus = usePluginWorkbench(null).panels.find((panel) => panel.service === MODELS_SERVICE);
  return (
    <InlineMessage tone="info">
      <span className="flex flex-wrap items-center justify-between gap-3">
        {title}
        <button
          className={settingButtonClass()}
          onClick={() =>
            router.push(
              crowpus === undefined ? pluginsPath() : pluginPagePath('installed', crowpus.plugin),
            )
          }
          type="button"
        >
          Set up Crowpus
        </button>
      </span>
    </InlineMessage>
  );
}

/**
 * Settings › Models: the default model for each thing a model makes, and the models offered.
 * Every model comes through Crowpus (its keys and quotas are the organization's); ElevenLabs'
 * models still come with its own key until Crowpus has a voice route.
 */
export function ModelsSettingsSection() {
  const [listing, setListing] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    () =>
      apiFetch<Listing>('/api/models').then(setListing, (reason: unknown) =>
        setError(message(reason)),
      ),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const rows = OUTPUTS.map((row) => ({
    ...row,
    models: (listing?.models ?? []).filter((model) => model.output.includes(row.output)),
  })).filter((row) => row.models.length > 0);
  const problems = listing?.problems ?? [];
  if (listing === null && error === null) return null;
  return (
    <>
      {error !== null && (
        <InlineMessage className="mb-4" tone="error">
          {error}
        </InlineMessage>
      )}
      {problems.map((problem) => (
        <InlineMessage className="mb-4" key={problem.provider} tone="warning">
          {problem.providerTitle}: {problem.message}
        </InlineMessage>
      ))}
      {error === null && rows.length === 0 && problems.length === 0 && (
        <NoModels title="No model available" />
      )}
      {rows.length > 0 && (
        <SettingsGroup title="Defaults">
          {rows.map((row) => {
            const chosen = listing?.defaults[row.output];
            return (
              <SettingRow key={row.output} title={row.title}>
                <SettingSelect
                  label={row.title}
                  onValueChange={(value) => {
                    const [provider, model] = value.split('\n');
                    void apiFetch(`/api/models/defaults/${row.output}`, {
                      method: 'PUT',
                      headers: { 'content-type': 'application/json' },
                      body: JSON.stringify(
                        value === AUTOMATIC || provider === undefined || model === undefined
                          ? null
                          : { provider, model },
                      ),
                    }).then(load, (reason: unknown) => setError(message(reason)));
                  }}
                  options={[
                    { value: AUTOMATIC, label: 'Automatic' },
                    ...row.models.map((model) => ({
                      value: pickerValue({ provider: model.provider, model: model.id }),
                      label: `${model.name} · ${model.providerTitle}`,
                    })),
                  ]}
                  value={chosen === undefined ? AUTOMATIC : pickerValue(chosen)}
                />
              </SettingRow>
            );
          })}
        </SettingsGroup>
      )}
      <AvailableModels models={listing?.models ?? []} title="Available" />
    </>
  );
}
